import React, { useContext, useEffect, useState } from 'react';
import { Form, Alert, ProgressBar } from 'react-bootstrap';
import { Utils } from '../../Services/Utils';
import { UserService } from '../../Services/UserService';
import { UserContext } from '../../Contexts';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faKey } from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { ZigzagStrip } from '../../Themes/Brand';

const SetPasswordComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle("Nastavenie hesla");
    }, []);
    let { currentUser }: any = useContext(UserContext);
    const { setPassword } = UserService();
    const [password, setPass]: [string, any] = useState('');
    const [confirmPassword, setConfirmPassword]: [string, any] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        setErrorMessage('');
    }, [password, confirmPassword]);
    const minLen = 8;

    const getPassVariant = () => {
        if (password.length === 0) return "secondary";     // sivá
        if (password.length < minLen) {
            return submitted ? "danger" : "warning";         // červená po submit, inak žltá
        }
        return "success";                                  // zelená
    };

    const passProgress = Math.min((password.length / minLen) * 100, 100);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true); // mark that user tried to submit

        if (password.length < minLen) {
            setErrorMessage(`Heslo musí mať aspoň ${minLen} znakov`);
            return;
        }
        if (password !== confirmPassword) {
            setErrorMessage('Heslá sa nezhodujú');
            return;
        }

        setPassword({ id: currentUser.id, password: password });
    };

    return (
        <div style={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem',
            backgroundColor: 'var(--st-canvas)',
        }}>
            <div style={{
                width: '100%',
                maxWidth: '26rem',
                backgroundColor: 'var(--st-paper)',
                borderRadius: 'var(--st-radius-card)',
                boxShadow: 'var(--st-shadow-card)',
                overflow: 'hidden',
            }}>
                <ZigzagStrip color="#6D57D4" />

                <div style={{ padding: '1.8rem 2rem 2rem' }}>
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2.7rem',
                            height: '2.7rem',
                            borderRadius: '11px',
                            backgroundColor: 'var(--st-accent-soft)',
                            marginBottom: '0.8rem',
                        }}>
                            <FontAwesomeIcon icon={faKey} style={{ color: 'var(--st-accent)', fontSize: '1rem' }} />
                        </div>
                        <h2 style={{
                            fontFamily: 'var(--st-font-display)',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: 'var(--st-text)',
                            margin: 0,
                        }}>
                            Vytvorte si heslo
                        </h2>
                        <p style={{ fontSize: '0.82rem', color: 'var(--st-text-faint)', margin: '0.4rem 0 0' }}>
                            Vaše dočasné heslo je potrebné nahradiť vlastným.
                        </p>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>Heslo</Form.Label>
                            <InputGroup>
                                <CustomTooltip placement="left" message={showPassword ? 'Skryť' : 'Zobraziť'}>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowPassword(p => !p)}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </Button>
                                </CustomTooltip>
                                <Form.Control
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Zadajte heslo"
                                    required
                                    autoFocus
                                    value={password}
                                    onChange={e => setPass(e.target.value)}
                                />
                            </InputGroup>
                            <ProgressBar style={{ maxHeight: '3px' }} animated={passProgress < 100} now={passProgress} variant={getPassVariant()} className="mt-2" />
                            <Form.Text
                                className={`fw-bolder text-${getPassVariant()}`}
                                style={{
                                    opacity: getPassVariant() === "success" ? 0 : 1,
                                    maxHeight: getPassVariant() === "success" ? 0 : "20px",
                                    display: "block",
                                    overflow: "hidden",
                                    transition: "all 0.4s ease"
                                }}
                            >
                                Minimálne {minLen} znakov
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mt-2">
                            <Form.Label>Potvrďte heslo</Form.Label>
                            <InputGroup>
                                <CustomTooltip placement="left" message={showConfirmPassword ? 'Skryť' : 'Zobraziť'}>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setShowConfirmPassword(p => !p)}
                                    >
                                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                    </Button>
                                </CustomTooltip>
                                <Form.Control
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Zopakujte heslo"
                                    required
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                />
                            </InputGroup>
                        </Form.Group>

                        <div className="d-grid mb-2 mt-4">
                            {errorMessage === '' ? (
                                <button type="submit" className="st-btn st-btn--fill" style={{ width: '100%' }}>
                                    Potvrdiť
                                </button>
                            ) : (
                                <Alert className="mt-1 p-2" variant="danger">
                                    {errorMessage}
                                </Alert>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default SetPasswordComponent;
